// 拒识码表 + 多码合并规则。文案移植自原型 eligibility-271.js（08/19 版），key 改为后端
// EligibilityStatusEnum 的拒识 token（后端已按 ELIGIBILITY_STATUS_MATRIX 把原始 AAA 码归并成枚举：
// MEMBER_ID_ERROR←64/72、DOB_MISMATCH←66/71、DOB_MISSING←58、PATIENT_NOT_FOUND←65/67/73/75（含姓名不匹配）、
// GENDER_ON_POLICY←74、NOT_SET_UP_WITH_PAYER←41/43/50/51/52/97、PAYER_DOWN←42/80；
// 68/76/78（group number）被后端静默丢弃，与原型 demo 面一致，本表不再收录）。
// 分工：后端下发 status token 列表，本表负责文案/修复建议/按钮/合并；未知 token 走 lookupAaa 兜底。
import { ECoverageStatus, EEligibilityOutcome, ENetworkStatus, TEligibilityCheckVo } from 'types/insuranceEligibility';

export type TAaaFixField = 'memberId' | 'dob' | 'dobMissing' | 'genderOnPolicy';

export type TAaaBucket = 'retryable' | 'terminal' | 'repairable';

export type TAaaEntry = {
    // 后端 status token（如 MEMBER_ID_ERROR）
    code: string;
    reason: string;
    short: string;
    plain: string;
    fix: string;
    fixField: TAaaFixField | null;
    bucket: TAaaBucket;
    // 未映射 token 兜底时为 true → 渲染 "not in our mapping table yet" 提示
    unmapped?: boolean;
};

const T = (
    code: string,
    reason: string,
    short: string,
    plain: string,
    fix: string,
    fixField: TAaaFixField | null,
    bucket: TAaaBucket,
): TAaaEntry => ({ code, reason, short, plain, fix, fixField, bucket });

const TERMINAL_FIX = 'Klarity has been notified and will follow up.';
const RETRY_FIX = 'Please try again in a few minutes.';

export const AAA_CODES: Record<string, TAaaEntry> = {
    // ── retryable：payer / 传输侧临时故障 ──
    PAYER_DOWN: T(
        'PAYER_DOWN',
        'Payer unable to respond at current time',
        'Payer system unavailable',
        'The payer is not able to answer eligibility requests right now. This is an outage on their side, not a problem with this patient’s coverage and not something on this record to correct.',
        RETRY_FIX,
        null,
        'retryable',
    ),
    SYSTEM_ERROR: T(
        'SYSTEM_ERROR',
        'The request could not be completed',
        'The check could not be completed',
        'The request did not go through, so coverage could not be verified. This does not indicate that the patient is uninsured.',
        RETRY_FIX,
        null,
        'retryable',
    ),
    // ── terminal：practice 与 payer 之间的配置问题，患者的卡解决不了 ──
    NOT_SET_UP_WITH_PAYER: T(
        'NOT_SET_UP_WITH_PAYER',
        'Provider not set up with this payer',
        'Not set up with this payer',
        'The payer would not accept eligibility requests from the practice. That is either because the practice is not registered with them yet, or because a detail on file does not match theirs. Either way it is not about this patient’s coverage.',
        TERMINAL_FIX,
        null,
        'terminal',
    ),
    // ── repairable：我们发出的字段与 payer 记录不匹配 ──
    MEMBER_ID_ERROR: T(
        'MEMBER_ID_ERROR',
        'Invalid/Missing Subscriber ID',
        'Member ID not recognised',
        'They do not recognise this member ID.',
        'Verify the Member ID against the insurance card, including any alpha prefix, then re-check. The payer did not query the name or date of birth, so leave those as they are.',
        'memberId',
        'repairable',
    ),
    PATIENT_NOT_FOUND: T(
        'PATIENT_NOT_FOUND',
        'Patient Not Found',
        'Patient not found',
        'They could not find this patient in their records.',
        'This usually means a typo, not missing coverage. Check Member ID, date of birth and name spelling against the card.',
        'memberId',
        'repairable',
    ),
    DOB_MISMATCH: T(
        'DOB_MISMATCH',
        'Patient date of birth does not match',
        'Date of birth does not match',
        'The date of birth we sent does not match their record for this member.',
        'Correct the date of birth to match the insurance card, then re-check.',
        'dob',
        'repairable',
    ),
    DOB_MISSING: T(
        'DOB_MISSING',
        'Invalid/Missing Date of Birth',
        'Date of birth missing or invalid',
        'The date of birth on this record was missing or not a usable date, so the payer could not look the patient up.',
        'Ask the patient to add their date of birth in their patient portal, then re-check.',
        'dobMissing',
        'repairable',
    ),
    // 保单上的行政字段，常与 chart 不一致且不该改 chart
    GENDER_ON_POLICY: T(
        'GENDER_ON_POLICY',
        'Invalid/Missing Subscriber Gender Code',
        'Gender on the policy does not match',
        'The payer’s record for this member lists a different gender than the one we sent. This is an administrative field on the policy and often does not match what is on the chart.',
        'Ask the patient which gender is listed on their insurance policy, then re-check under that. It does not change anything on their chart.',
        'genderOnPolicy',
        'repairable',
    ),
};

// 未映射 token 兜底：通用文案 + unmapped 标记（→ 卡内提示 Billing Ops 已被通知）
export const lookupAaa = (code: string): TAaaEntry =>
    AAA_CODES[String(code)] || {
        code: String(code),
        reason: `Payer rejection (${code})`,
        short: `Payer rejection (${code})`,
        plain: 'The payer rejected this request but did not give a reason we recognise.',
        fix: 'Verify the patient’s details against their insurance card. If the details are correct, contact Klarity support.',
        fixField: null,
        bucket: 'terminal',
        unmapped: true,
    };

// ── 多码合并（08/17 规则；后端多状态逗号串按声明序即多码）──
// 字段归桶：insurance（卡片能解决）/ profile（要问患者本人）
export const FIELD_BUCKET: Partial<Record<TAaaFixField, 'insurance' | 'profile'>> = {
    memberId: 'insurance',
    dob: 'profile',
    dobMissing: 'profile',
    genderOnPolicy: 'profile',
};

// profile 优先：错误的 DOB 往往正是 member ID 查不到的根因；反之新卡永远解决不了 DOB
export const BUCKET_PRIORITY: Array<'profile' | 'insurance'> = ['profile', 'insurance'];

// 每字段的动作（Edit 已于 08/17 全部移除：没人能凭空造出正确的 ID/姓名）
export const FIX_ACTIONS: Partial<Record<TAaaFixField, Array<'card' | 'message'>>> = {
    memberId: ['card'],
    dob: ['message'],
    dobMissing: ['message'],
    genderOnPolicy: ['message'],
};

export const FIX_FIELD_LABELS: Partial<Record<TAaaFixField, string>> = {
    memberId: 'Member ID',
    dob: 'Date of birth',
    dobMissing: 'Date of birth',
    genderOnPolicy: 'Gender on file',
};

export type TMergedRejection = {
    aaas: TAaaEntry[];
    // 本轮出按钮的码（lead bucket）；deferred 是被推迟的另一桶（一行灰字，不出第二组按钮）
    acted: TAaaEntry[];
    deferred: TAaaEntry[];
    multi: boolean;
    // bar 摘要：单码 short，多码 short 用 " + " 连接（不隐藏任何码）
    barLine: string;
    // 任一硬失败 → bad；否则 problem（当前码表全为 soft）
    tone: 'problem' | 'bad';
    // 合并后的动作（去重，结构上最多 card + message 各一个）
    actions: Array<'card' | 'message'>;
    // 合并后的 Recommended fix 文案（多码 = acted 各 fix 拼接）
    combinedFix: string;
};

export const mergeRejection = (codes: string[]): TMergedRejection => {
    const aaas = (codes.length ? codes : ['']).map(lookupAaa);
    const multi = aaas.length > 1;

    const bucketsPresent: Array<'profile' | 'insurance'> = [];
    if (multi) {
        aaas.forEach((x) => {
            const bk = x.fixField ? FIELD_BUCKET[x.fixField] : undefined;
            if (bk && !bucketsPresent.includes(bk)) bucketsPresent.push(bk);
        });
    }
    const leadBucket = bucketsPresent.length > 1 ? BUCKET_PRIORITY.filter((b) => bucketsPresent.includes(b))[0] : null;
    const acted = leadBucket ? aaas.filter((x) => x.fixField && FIELD_BUCKET[x.fixField] === leadBucket) : aaas;
    const deferred = leadBucket ? aaas.filter((x) => !x.fixField || FIELD_BUCKET[x.fixField] !== leadBucket) : [];

    const actions: Array<'card' | 'message'> = [];
    acted.forEach((x) => {
        if (!x.fixField) return;
        (FIX_ACTIONS[x.fixField] || []).forEach((act) => {
            if (!actions.includes(act)) actions.push(act);
        });
    });

    return {
        aaas,
        acted,
        deferred,
        multi,
        barLine: aaas.map((x) => x.short || x.reason).join(' + '),
        tone: 'problem',
        actions,
        combinedFix: multi ? acted.map((x) => x.fix).join(' ') : aaas[0].fix,
    };
};

// 按下某按钮时反查它属于哪个 AAA（保证 Message patient 草稿讲的是对应字段）
export const aaaForAction = (merged: TMergedRejection, action: 'card' | 'message'): TAaaEntry | undefined =>
    merged.acted.find((x) => x.fixField && (FIX_ACTIONS[x.fixField] || []).includes(action));

// outreach（二次失败）序列适用的三类失败：非终态拒识 / inactive / payer 明说 out-of-network。
// 终态码排除——那是 practice 与 payer 之间的配置问题，给患者发消息无济于事；
// unmapped / 无结果也排除（请求从未发出）。放在本文件因为要查码表的 bucket
export const isFailureResult = (check?: TEligibilityCheckVo): boolean => {
    if (!check) return false;
    if (check.outcome === EEligibilityOutcome.REJECTED || !check.result) {
        const codes = check.rejection?.codes || [];
        return codes.some((c) => lookupAaa(c).bucket !== 'terminal');
    }
    if (check.result.coverageStatus === ECoverageStatus.INACTIVE) return true;
    return check.result.network?.status === ENetworkStatus.OUT_OF_NETWORK;
};

// 终态码的 Ops reference：按 clientId + checkedAt 稳定 hash，截图可复现
export const opsRef = (clientId: number, checkedAt: string): string => {
    const src = `${clientId}-${checkedAt}`;
    let h = 0;
    for (let i = 0; i < src.length; i += 1) {
        // eslint-disable-next-line no-bitwise
        h = (h * 31 + src.charCodeAt(i)) >>> 0;
    }
    return `ELG-${String(h).slice(0, 6).padStart(6, '0')}`;
};
